import { useMemo, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useApp } from '../AppContext'
import { useAsync } from '../hooks/useAsync'
import { hasCapability } from '../auth/roles'
import { PageHeader } from '../components/primitives/PageHeader'
import { Panel } from '../components/primitives/Panel'
import { DataTable, type DataTableColumn } from '../components/primitives/DataTable'
import { Fact, FactList, Mono } from '../components/primitives/Fact'
import { EnvironmentStatusChip } from '../components/primitives/StatusChip'
import { Field } from '../components/primitives/Field'
import { Notice } from '../components/primitives/Notice'
import { ConfirmAdjustmentModal } from '../components/ConfirmAdjustmentModal'
import { refusalSentence } from '../theme/vocabulary'
import { ApiError } from '../api/client'
import type {
  AccountDetail,
  AccountUsageMigration,
  AccountUsageResponse,
  EnvironmentListItem,
  EnvironmentListResponse,
  MigrationBalance,
  MigrationLicenseType,
} from '../api/types'

// THE COPY THAT MUST LAND EVEN IF NOTHING ELSE HERE DOES (R-ATC-12 /
// `ATC-ADR-013`). `reservedOpen` and `pool.inUse` used to count MIGRATIONS and
// now count DEVICES. An operator reads these numbers before a sales
// conversation, and an unexplained tripling is worse than no number at all —
// so the meaning is stated at the point of use rather than left to be inferred
// from a figure that silently changed.
const PER_DEVICE_UNIT_HINT =
  'Counts in-flight DEVICES, not migrations. Since per-device licensing, one unit — a consumable ' +
  'credit, or a pool slot — is reserved per device in an activated wave, so a 300-device wave holds ' +
  '300 and not 1. A campaign-era migration reserved before that change still holds exactly 1.'

/**
 * Was this refusal the ROUTE being absent rather than the request being wrong?
 *
 * Every ATC handler answers inside the standard response envelope and always
 * carries an `error.code` — this endpoint's only 404 is `account_not_found`. A
 * 404 with NO code is the Functions host saying it has no such route, which is
 * exactly "ATC-148 is not deployed here yet".
 *
 * Deliberately narrow. A 500, a network failure or a coded 404 all fall
 * through and surface as a real error, because "the section vanishes" must
 * mean one specific thing and not "something went wrong".
 */
function isUsageRouteAbsent(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.code === undefined
}

/**
 * The current calendar month as two `YYYY-MM-DD` values, `to` EXCLUSIVE.
 *
 * Computed in UTC and by month arithmetic (`Date.UTC(y, m + 1, 1)` rolls
 * December into January on its own), so the default window is the one the
 * monthly true-up actually asks for and adjacent months partition the ledger
 * exactly — which is the property the server's exclusive upper bound exists to
 * give.
 */
function currentMonthRange(): { from: string; to: string } {
  const now = new Date()
  const first = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
  const next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)
  const day = (ms: number) => new Date(ms).toISOString().slice(0, 10)
  return { from: day(first), to: day(next) }
}

/**
 * `YYYY-MM-DD` to the full ISO-8601 instant the server requires.
 *
 * The server REFUSES a bare date rather than assigning it midnight or
 * end-of-day, because either convention is a silent 24-hour discrepancy at a
 * period boundary. This console picks midnight UTC and SAYS SO on the field
 * hints — an explicit, visible convention rather than a silent one — and
 * builds the string rather than going through `Date`, so no local timezone can
 * shift a period boundary.
 */
function startOfDayInstant(date: string): string {
  return `${date}T00:00:00.000Z`
}

/**
 * Whether ATC-148's route answered at all, kept in the fetched value rather
 * than inferred from an error string: the section must be ABSENT when the
 * endpoint is not deployed, and absent is a different state from failed.
 */
type UsageProbe = { available: true; usage: AccountUsageResponse } | { available: false }

// Customer detail (ATC-130). The balance is fetched HERE — exactly one ledger
// scan, for the one customer being looked at — never per row on the list.
export function CustomerDetail() {
  const { accountId = '' } = useParams()
  const { api, role } = useApp()
  const canGrant = hasCapability(role, 'billing')

  const [nonce, setNonce] = useState(0)

  const environments = useAsync<EnvironmentListResponse>(
    () => api.listEnvironments({ accountId }),
    [accountId, nonce],
  )
  const balance = useAsync<MigrationBalance & { accountId: string }>(
    () => api.getAccountBalance(accountId),
    [accountId, nonce],
  )
  const account = useAsync<AccountDetail>(() => api.getAccount(accountId), [accountId, nonce])

  // THE REGISTRY IS THE SOURCE OF TRUTH FOR THE NAME (ATC-137).
  //
  // This page used to derive its title from the environment membership rows,
  // because until ATC-137 no endpoint exposed the registry row. A customer with
  // nothing mapped therefore rendered as "(unnamed customer)" on its own detail
  // page while the customers LIST — reading this same registry — showed the
  // name correctly.
  //
  // The membership copy is the PRE-registry name field: it exists because an
  // account used to be created by being mentioned and had nowhere else to keep
  // a name. It is kept here ONLY as a degradation path, so a console deployed
  // ahead of ATC-137's backend falls back to its previous behaviour rather than
  // blanking the title. It is not a second source and must never win over a
  // successful registry read — `Set-AtcAccountRegistryTouch` does not overwrite
  // a non-blank registry name, so the two genuinely can disagree.
  // ATC-141: the membership fallback is GONE. It existed only so a console
  // deployed ahead of ATC-137's backend degraded to its previous behaviour
  // rather than blanking the title; that endpoint is deployed, and this page no
  // longer reads the membership rows at all, so the fallback had no source left
  // to read. The registry is now the single source of the customer's name —
  // which is what ATC-137 set out to make true.
  const accountName = account.data?.accountName ?? ''

  // --- Grant ---
  const [grantType, setGrantType] = useState<MigrationLicenseType>('consumable')
  const [grantAmount, setGrantAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionOk, setActionOk] = useState<string | null>(null)

  // --- Rename ---
  const canWrite = hasCapability(role, 'write')
  const [renaming, setRenaming] = useState(false)
  const [renameInput, setRenameInput] = useState('')

  // --- Adjustment ---
  const [adjustType, setAdjustType] = useState<MigrationLicenseType>('pool')
  const [adjustAmount, setAdjustAmount] = useState('')
  const [adjustReason, setAdjustReason] = useState('')
  const [confirmingAdjust, setConfirmingAdjust] = useState(false)

  // --- Usage by migration (ATC-148 / R-ATC-12) ---
  //
  // One object, computed once, so a page that renders across a month boundary
  // cannot take `from` from one month and `to` from the next.
  const initialUsageRange = useMemo(currentMonthRange, [])
  const [usageFrom, setUsageFrom] = useState(initialUsageRange.from)
  const [usageTo, setUsageTo] = useState(initialUsageRange.to)
  // The APPLIED window, separate from the two draft inputs: the read is one
  // ledger partition scan, and a scan per keystroke is not something to put on
  // a page an operator opens to look at a balance.
  const [usageRange, setUsageRange] = useState(initialUsageRange)

  // NOT keyed on `nonce`. A grant or an adjustment changes the BALANCE, not the
  // reservation/settlement rows this reads, so re-running the scan after one
  // would cost a partition scan for an identical answer.
  const usage = useAsync<UsageProbe>(async () => {
    try {
      return {
        available: true,
        usage: await api.getAccountUsage(accountId, {
          from: startOfDayInstant(usageRange.from),
          to: startOfDayInstant(usageRange.to),
        }),
      }
    } catch (err) {
      if (isUsageRouteAbsent(err)) {
        return { available: false }
      }
      throw err
    }
  }, [accountId, usageRange])

  const usageProbe = usage.data
  const usageRouteAbsent = usageProbe?.available === false
  const usageReport = usageProbe && usageProbe.available ? usageProbe.usage : null
  // Rendered only once we know the route answered — so a not-deployed endpoint
  // leaves NOTHING behind, rather than flashing a panel that then disappears.
  // A real failure still shows the panel with its error, because hiding a 500
  // and hiding an absent route would be indistinguishable to the operator.
  const showUsage = !usageRouteAbsent && (usageProbe !== null || usage.error !== null)
  const usageWindowValid = usageFrom !== '' && usageTo !== '' && usageFrom < usageTo

  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      return refusalSentence(err.code, err.message)
    }
    return err instanceof Error ? err.message : String(err)
  }

  async function run(action: () => Promise<string>) {
    setBusy(true)
    setActionError(null)
    setActionOk(null)
    try {
      setActionOk(await action())
      setNonce((n) => n + 1)
    } catch (err) {
      setActionError(describe(err))
      throw err
    } finally {
      setBusy(false)
    }
  }

  function submitGrant(e: FormEvent) {
    e.preventDefault()
    const amount = Number.parseInt(grantAmount, 10)
    if (!Number.isInteger(amount) || amount < 1) {
      // The `≥` is a mathematical operator in copy, NOT an icon.
      setActionError('Enter a whole number ≥ 1.')
      return
    }
    void run(async () => {
      const result = await api.grantMigrationLicense(accountId, grantType, amount)
      setGrantAmount('')
      return `Granted ${result.amount} ${
        result.licenseType === 'pool' ? 'pool capacity' : 'consumable credit(s)'
      } (grant ${result.grantId}).`
    }).catch(() => undefined)
  }

  function submitRename(e: FormEvent) {
    e.preventDefault()
    const next = renameInput.trim()
    if (!next) {
      // Mirrors the server's `account_name_missing`. A blank is what
      // Set-AtcAccountRegistryTouch reads as "not yet known", so there is
      // deliberately no clear-the-name action to offer here either.
      setActionError('Enter a customer name.')
      return
    }
    void run(async () => {
      const result = await api.renameAccount(accountId, next)
      setRenaming(false)
      setRenameInput('')
      return result.changed
        ? `Renamed to ${result.accountName}.`
        : `Already named ${result.accountName}; nothing changed.`
    }).catch(() => undefined)
  }

  const adjustMagnitude = Number.parseInt(adjustAmount, 10)
  const adjustValid =
    Number.isInteger(adjustMagnitude) && adjustMagnitude > 0 && adjustReason.trim().length > 0

  const currentTotal =
    adjustType === 'pool'
      ? (balance.data?.pool.capacity ?? 0)
      : (balance.data?.consumable.granted ?? 0)
  const committed =
    adjustType === 'pool'
      ? (balance.data?.pool.inUse ?? 0)
      : (balance.data?.consumable.burned ?? 0) + (balance.data?.consumable.reservedOpen ?? 0)

  function confirmAdjustment() {
    void run(async () => {
      const result = await api.createAdjustment({
        accountId,
        licenseType: adjustType,
        amount: -adjustMagnitude,
        reason: adjustReason.trim(),
      })
      setConfirmingAdjust(false)
      setAdjustAmount('')
      setAdjustReason('')
      return `Adjusted ${result.licenseType} by ${result.amount} (adjustment ${result.adjustmentId}).`
    }).catch(() => undefined)
  }

  // ATC-141. The "Account name" column is GONE, and it had to go: it rendered
  // the membership row's `accountName`, which is the PRE-registry copy of the
  // customer's name. ATC-138 stopped the environment page sending it and
  // ATC-140 stopped the wizard, so on anything provisioned since it is
  // permanently blank — a column headed "Account name" reading "No name" on
  // the account's own page, beside a title carrying that very name.
  //
  // It is replaced by the environment's LABEL, which is what an operator
  // actually wants here and which the membership rows do not carry. Hence the
  // switch to `listEnvironments({ accountId })`: same read model the
  // environments list uses, updated by the map (Set-AtcEnvironmentRegistryAccountId),
  // and it carries status and expiry too.
  //
  // THE TRADE-OFF, STATED: the membership table is the source of truth and the
  // registry is a best-effort projection, so a registry write that failed would
  // hide an environment here. That same failure already hides it from the
  // environments list, so this makes the two views agree rather than
  // introducing a new blind spot — and a divergence is repaired by the next
  // write or a backfill.
  const columns: DataTableColumn<EnvironmentListItem>[] = [
    {
      key: 'environment',
      header: 'Environment',
      render: (e) => (
        <Link to={`/environments/${e.customerEnvironmentId}`}>
          {e.customerName || <em>(unnamed)</em>}
        </Link>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (e) => <EnvironmentStatusChip status={e.status} />,
    },
    {
      key: 'entitlements',
      header: 'Entitlements',
      render: (e) => e.entitledServices.join(', '),
    },
    {
      key: 'expires',
      header: 'Expires',
      render: (e) =>
        e.expiresAt ? <Mono>{e.expiresAt}</Mono> : <span className="muted">never</span>,
    },
  ]

  // `mode` is rendered as its RAW WIRE VALUE — `device` or `campaign` — and
  // deliberately not mapped to a label or a chip.
  //
  // It is what an operator greps the ledger for, it is self-explanatory in
  // English, and an unrecognised value therefore passes through legibly instead
  // of falling through a mapping (the rule `migrationTypeLabel` already
  // follows). A chip would need a tone mapping, and tone mappings live in
  // `theme/statusTone.ts` — one module, never a second one invented per page.
  const usageColumns: DataTableColumn<AccountUsageMigration>[] = [
    { key: 'migration', header: 'Migration', render: (m) => <Mono>{m.migrationId}</Mono> },
    {
      key: 'mode',
      header: 'Mode',
      render: (m) => (m.mode ? m.mode : <span className="muted">not recorded</span>),
    },
    { key: 'bucket', header: 'Bucket', render: (m) => m.bucket },
    { key: 'reserved', header: 'Reserved', numeric: true, render: (m) => m.reservedCount },
    { key: 'burned', header: 'Burned', numeric: true, render: (m) => m.burnedCount },
    { key: 'released', header: 'Released', numeric: true, render: (m) => m.releasedCount },
    {
      key: 'settled',
      header: 'Settled',
      // "No value" is not "missing data": an open wave says so in words. The
      // server sends `null` rather than `''` for exactly this reason.
      render: (m) =>
        m.settledAt ? <Mono>{m.settledAt}</Mono> : <span className="muted">in flight</span>,
    },
  ]

  return (
    <section>
      <PageHeader
        back={
          <Link to="/customers" className="rx-page-back">
            <ArrowLeft size={14} strokeWidth={1.5} aria-hidden="true" /> Customers
          </Link>
        }
        title={accountName || <em>(unnamed customer)</em>}
        subtitle={<Mono>{accountId}</Mono>}
        actions={
          canWrite && account.data ? (
            <button
              type="button"
              onClick={() => {
                setRenameInput(accountName)
                setRenaming((r) => !r)
              }}
            >
              Rename
            </button>
          ) : undefined
        }
      />

      {renaming && (
        // A plain confirm, not the amber→red escalation. That pattern was
        // promoted to suite Tier 1 for an IRREVERSIBLE re-key; a rename is
        // reversible in ten seconds, and diluting the escalation across every
        // serious-but-reversible action is how it stops meaning anything.
        <form className="inline-form" onSubmit={submitRename}>
          <Field label="Customer name">
            <input
              type="text"
              value={renameInput}
              maxLength={128}
              onChange={(e) => setRenameInput(e.target.value)}
            />
          </Field>
          <div className="row">
            <button type="submit" disabled={busy || !renameInput.trim()}>
              Save name
            </button>
            <button type="button" onClick={() => setRenaming(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {actionError && <Notice tone="error">{actionError}</Notice>}
      {actionOk && <Notice tone="ok">{actionOk}</Notice>}

      <div className="cards">
        <Panel title="Migration licenses">
          {balance.error && <Notice tone="error">Could not load the balance.</Notice>}
          {balance.data && (
            <FactList>
              <Fact label="Consumable available">
                <Mono>
                  {balance.data.consumable.available} / {balance.data.consumable.granted}
                </Mono>
              </Fact>
              <Fact label="Consumable burned">
                <Mono>{balance.data.consumable.burned}</Mono>
              </Fact>
              {/* THE UNIT CHANGED UNDER THIS NUMBER (R-ATC-12). It counted
                  migrations and now counts devices, so it is labelled with its
                  unit inline AND carries the hint on hover. The two are not
                  redundant: a hover tooltip reaches neither a keyboard nor a
                  touch operator, and the visible sentence below the list is
                  what actually guarantees the meaning is stated. */}
              <Fact label="Consumable reserved (open)">
                <span title={PER_DEVICE_UNIT_HINT}>
                  <Mono>{balance.data.consumable.reservedOpen}</Mono> devices
                </span>
              </Fact>
              <Fact label="Pool available">
                <Mono>
                  {balance.data.pool.available} / {balance.data.pool.capacity}
                </Mono>
              </Fact>
              <Fact label="Pool in use">
                {/* A pool slot is now held per in-flight DEVICE too — the pool
                    finally models what it always claimed to. */}
                <span title={PER_DEVICE_UNIT_HINT}>
                  <Mono>{balance.data.pool.inUse}</Mono> devices
                </span>
              </Fact>
              <Fact label="Reclaimed (open)">
                <Mono>{balance.data.pool.capacity >= 0 ? balance.data.reclaimedOpen : 0}</Mono>
              </Fact>
              {/* INFORMATIONAL, never arithmetic: the signed sum of corrections,
                  always <= 0. It answers "why does this say 40 when I granted
                  50", which the net figures alone cannot. Shown only when
                  non-zero so an uncorrected customer's panel stays quiet. */}
              {(balance.data.consumable.adjustments !== 0 ||
                balance.data.pool.adjustments !== 0) && (
                <Fact label="Corrections">
                  <Mono>
                    consumable {balance.data.consumable.adjustments}, pool{' '}
                    {balance.data.pool.adjustments}
                  </Mono>
                </Fact>
              )}
            </FactList>
          )}

          {balance.data && (
            <p className="muted">
              Reserved and in-use figures count <strong>devices</strong>, not migrations: one unit is
              held per device in an activated wave. A migration started before per-device licensing
              still holds exactly one, and shows as <strong>campaign</strong> mode in the usage table.
            </p>
          )}

          {canGrant && (
            <form className="inline-form" onSubmit={submitGrant}>
              <div className="row">
                <Field label="Grant">
                  <select
                    value={grantType}
                    onChange={(e) => setGrantType(e.target.value as MigrationLicenseType)}
                  >
                    <option value="consumable">Consumable credits</option>
                    {/* A pool grant is ADDITIVE. The old label said
                        "provision / resize", which is wrong in a way that
                        costs money: granting 10 twice yields 20, not 10. An
                        operator reading "resize" and typing the target number
                        doubles the pool. */}
                    <option value="pool">Pool capacity (adds to the existing pool)</option>
                  </select>
                </Field>
                <Field label="Amount">
                  <input
                    type="number"
                    min={1}
                    step={1}
                    placeholder="Amount"
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)}
                  />
                </Field>
                <button type="submit" className="btn-primary" disabled={busy || !grantAmount}>
                  {busy ? 'Working…' : 'Grant'}
                </button>
              </div>
              <p className="muted">
                Grants add to the current balance. To correct an over-grant, use the reduction
                below — the ledger is append-only, so a correction is a new row rather than an edit.
              </p>
            </form>
          )}

          {canGrant && (
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault()
                setActionError(null)
                setConfirmingAdjust(true)
              }}
            >
              <div className="row">
                <Field label="Reduce">
                  <select
                    value={adjustType}
                    onChange={(e) => setAdjustType(e.target.value as MigrationLicenseType)}
                  >
                    <option value="pool">Pool capacity</option>
                    <option value="consumable">Consumable credits</option>
                  </select>
                </Field>
                <Field label="Reduce by">
                  <input
                    type="number"
                    min={1}
                    step={1}
                    placeholder="Amount"
                    value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)}
                  />
                </Field>
                <Field label="Reason" required>
                  <input
                    type="text"
                    placeholder="Provisioned 5000 instead of 500"
                    value={adjustReason}
                    maxLength={256}
                    onChange={(e) => setAdjustReason(e.target.value)}
                  />
                </Field>
                {/* THE AMBER TRIGGER. Its red confirm lives in
                    ConfirmAdjustmentModal — a sibling of ConfirmRotateModal,
                    which is not refactored or touched. */}
                <button type="submit" className="btn-warn" disabled={busy || !adjustValid}>
                  Reduce balance…
                </button>
              </div>
            </form>
          )}
        </Panel>

        <Panel title="Environments" flush>
          <DataTable
            caption="Environments mapped to this customer"
            columns={columns}
            data={environments.data?.environments ?? []}
            keyForRow={(e) => e.customerEnvironmentId}
            isLoading={environments.loading}
            error={environments.error ? 'Could not load environments.' : undefined}
            emptyState="No environments mapped to this customer."
          />
        </Panel>
      </div>

      {/* ABSENT, NOT BROKEN. When ATC-148's route is not deployed in this
          environment the whole section does not render — no error banner, no
          empty skeleton. A 500 or any other refusal still renders the panel
          carrying its error, because "endpoint missing" and "endpoint broken"
          must not look the same to an operator. */}
      {showUsage && (
        <Panel title="Usage by migration">
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault()
              setUsageRange({ from: usageFrom, to: usageTo })
            }}
          >
            <div className="row">
              <Field label="From (inclusive)" hint="00:00 UTC on this date">
                <input
                  type="date"
                  value={usageFrom}
                  onChange={(e) => setUsageFrom(e.target.value)}
                />
              </Field>
              <Field label="To (exclusive)" hint="00:00 UTC on this date — the day itself is not counted">
                <input type="date" value={usageTo} onChange={(e) => setUsageTo(e.target.value)} />
              </Field>
              <button type="submit" disabled={!usageWindowValid}>
                Show usage
              </button>
            </div>
          </form>

          {usage.error && <Notice tone="error">Could not load usage. {usage.error}</Notice>}

          {usageReport && (
            <FactList>
              <Fact label="Window">
                <Mono>{usageReport.window.from}</Mono> to <Mono>{usageReport.window.to}</Mono>{' '}
                (exclusive), anchored on {usageReport.window.anchor}
              </Fact>
              <Fact label="Migrations">
                <Mono>{usageReport.totals.migrations}</Mono>
              </Fact>
              <Fact label="Devices reserved">
                <Mono>{usageReport.totals.reservedCount}</Mono> (consumable{' '}
                <Mono>{usageReport.totals.consumable.reservedCount}</Mono>, pool{' '}
                <Mono>{usageReport.totals.pool.reservedCount}</Mono>)
              </Fact>
              <Fact label="Devices burned">
                <Mono>{usageReport.totals.burnedCount}</Mono> (consumable{' '}
                <Mono>{usageReport.totals.consumable.burnedCount}</Mono>, pool{' '}
                <Mono>{usageReport.totals.pool.burnedCount}</Mono>)
              </Fact>
              <Fact label="Devices released">
                <Mono>{usageReport.totals.releasedCount}</Mono>
              </Fact>
              {/* Expected to be 0. Anything else is an operator signal rather
                  than a rounding detail — on a billing read a quietly missing
                  migration is a quietly wrong invoice — so it is shown only
                  when it would otherwise go unnoticed. */}
              {usageReport.totals.unanchored !== 0 && (
                <Fact label="Unanchored reservations">
                  <Mono>{usageReport.totals.unanchored}</Mono> — excluded from this window because
                  their reservation time could not be read
                </Fact>
              )}
            </FactList>
          )}

          <DataTable
            caption="Migration consumption in the selected window"
            columns={usageColumns}
            data={usageReport?.migrations ?? []}
            keyForRow={(m) => m.migrationId}
            isLoading={usage.loading}
            emptyState="No migrations were reserved in this window."
          />
        </Panel>
      )}

      {confirmingAdjust && (
        <ConfirmAdjustmentModal
          accountId={accountId}
          accountName={accountName}
          licenseType={adjustType}
          amount={-adjustMagnitude}
          reason={adjustReason.trim()}
          currentTotal={currentTotal}
          committed={committed}
          busy={busy}
          error={actionError}
          onConfirm={confirmAdjustment}
          onCancel={() => setConfirmingAdjust(false)}
        />
      )}
    </section>
  )
}
